import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
  Grid,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import { useToken } from '../../../hooks/useToken';

/**
 * Modal para crear una nueva columna en un tablero Kanban
 * @param {boolean} open - Estado que controla si el modal está abierto
 * @param {Function} onClose - Función para cerrar el modal
 * @param {Function} onSave - Función para guardar la columna
 * @param {string|number} boardId - ID del tablero al que pertenece la columna
 * @returns {JSX.Element} Componente de modal
 */
const CreateColumnModal = ({ open, onClose, onSave, boardId }) => {
  const { getTokenSilent } = useToken();
  const agent = useSelector(state => state.agent);
  // Adaptación a la nueva estructura del estado después de la refactorización
  const { columns: { items: columns = [] } = { items: [] } } = useSelector(state => state.kanban);

  // Estados para el control del modal
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados para los campos del formulario
  const [columnName, setColumnName] = useState('');
  const [columnDescription, setColumnDescription] = useState('');
  const [nameError, setNameError] = useState('');

  // Estado para manejar metadatos adicionales
  const [metaDataFields, setMetaDataFields] = useState([]);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [showAddMetaField, setShowAddMetaField] = useState(false);

  // Determinar el tipo de tablero basado en la URL
  const isSupportBoard = window.location.pathname.includes('/soporte');

  /**
   * Resetea el formulario a su estado inicial
   */
  const resetForm = () => {
    setColumnName('');
    setColumnDescription('');
    setMetaDataFields([]);
    setNewKey('');
    setNewValue('');
    setShowAddMetaField(false);
    setNameError('');
    setError(null);
  };

  // Resetear el formulario cuando se cierra el modal
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  /**
   * Maneja cambios en el campo de nombre de columna
   * @param {Object} e - Evento de cambio
   */
  const handleNameChange = (e) => {
    setColumnName(e.target.value);
    if (e.target.value.trim()) {
      setNameError('');
    }
  };

  /**
   * Maneja cambios en el campo de descripción
   * @param {Object} e - Evento de cambio
   */
  const handleDescriptionChange = (e) => {
    setColumnDescription(e.target.value);
  };

  /**
   * Añade un nuevo campo de metadatos al formulario
   */
  const handleAddMetaField = () => {
    if (newKey.trim() && newValue.trim()) {
      setMetaDataFields([
        ...metaDataFields,
        { key: newKey.trim(), value: newValue.trim() }
      ]);
      setNewKey('');
      setNewValue('');
      setShowAddMetaField(false);
    }
  };

  /**
   * Elimina un campo de metadatos del formulario
   * @param {number} index - Índice del campo a eliminar
   */
  const handleRemoveMetaField = (index) => {
    // Crear una copia del array para no modificar el original directamente
    const updatedFields = metaDataFields.filter((_, idx) => idx !== index);

    // Actualizar el estado con el nuevo array (sin el elemento eliminado)
    setMetaDataFields(updatedFields);
  };

  /**
   * Construye el objeto de metadatos en formato JSON
   * @returns {string|null} String JSON de metadatos o null si no hay datos
   */
  const buildMetaData = () => {
    // Crear un objeto vacío para los metadatos
    const metaData = {};

    // Añadir todos los campos de metadatos del formulario
    metaDataFields.forEach(field => {
      if (field.key && field.key.trim()) { // Verificar que la clave no esté vacía
        metaData[field.key] = field.value;
      }
    });

    // Añadir la descripción si existe
    if (columnDescription.trim()) {
      metaData.description = columnDescription.trim();
    }

    // Determinar el tipo de tablero basado en la URL o el contexto
    if (isSupportBoard) {
      // Si estamos en un tablero de soporte, añadir el tipo
      metaData.type = 'soporte';
    }
    // Eliminamos la asignación de type: 'general' para tableros generales

    return Object.keys(metaData).length > 0 ? JSON.stringify(metaData) : null;
  };

  /**
   * Calcula dinámicamente la última posición para una nueva columna
   * @returns {number} La posición siguiente a la última columna existente
   */
  const getLastPosition = () => {
    // Si no hay columnas, devolver 2 como posición inicial (después de Ticket Creado)
    if (!columns || !Array.isArray(columns) || columns.length === 0) {
      return 2;
    }

    // Filtrar las columnas que pertenecen a este tablero
    const boardColumns = columns.filter(col =>
      String(col.board_id) === String(boardId)
    );

    if (boardColumns.length === 0) {
      return 2; // Si no hay columnas en el tablero, devolver posición 2
    }

    // Encontrar la columna "Ticket Creado"
    const ticketCreadoColumn = boardColumns.find(col => 
      col.name.toLowerCase() === 'ticket creado'
    );

    // Si no existe Ticket Creado, devolver posición 2
    if (!ticketCreadoColumn) {
      return 2;
    }

    // Obtener todas las columnas excepto Ticket Creado
    const otherColumns = boardColumns.filter(col => 
      col.name.toLowerCase() !== 'ticket creado'
    );

    // Si no hay otras columnas, devolver posición 2
    if (otherColumns.length === 0) {
      return 2;
    }

    // Encontrar la posición más alta entre las columnas existentes (excluyendo Ticket Creado)
    const maxPosition = otherColumns.reduce((max, column) => {
      const position = parseInt(column.position, 10) || 0;
      return position > max ? position : max;
    }, 0);

    // Devolver la siguiente posición disponible
    return maxPosition + 1;
  };

  /**
   * Función para validar todos los campos del formulario
   * @returns {boolean} true si el formulario es válido
   */
  const validateForm = () => {
    let isValid = true;

    if (!columnName.trim()) {
      setNameError('El nombre de la columna es obligatorio');
      isValid = false;
    }

    if (!agent || !agent.id_customer) {
      setError('No se encontró información del cliente');
      isValid = false;
    }

    return isValid;
  };

  /**
   * Capitaliza la primera letra de un texto
   * @param {string} text - Texto a capitalizar
   * @returns {string} Texto con la primera letra en mayúscula
   */
  const capitalizeFirstLetter = (text) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  /**
   * Maneja el proceso de guardar la columna
   */
  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const token = await getTokenSilent();

      if (!token) {
        throw new Error('No se pudo obtener el token de autenticación');
      }

      if (!boardId) {
        throw new Error('ID del tablero no especificado');
      }

      // Obtener la posición para la nueva columna
      const newPosition = getLastPosition();

      // Preparar los datos de la columna
      const columnData = {
        name: capitalizeFirstLetter(columnName.trim()),
        position: newPosition,
        id_customer: agent.id_customer,
        token
      };

      // Construir los metadatos
      const metaDataJson = buildMetaData();
      if (metaDataJson) {
        columnData.meta_data = metaDataJson;
      }
      
      // Crear la nueva columna (el reordenamiento se hace en el padre)
      await onSave(columnData);

      resetForm();
      onClose();
    } catch (error) {
      console.error('Error al crear columna:', error);
      setError(error.message || 'Error al crear la columna');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Maneja el cierre del modal
   */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  /**
   * Renderiza la sección de metadatos
   * @returns {JSX.Element} Sección de metadatos
   */
  const renderMetadataSection = () => (
    <Box sx={{ mt: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1">Información adicional</Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={() => setShowAddMetaField(true)}
          disabled={loading}
          variant="outlined"
          size="small"
        >
          Añadir campo
        </Button>
      </Box>

      {/* Formulario para añadir metadatos */}
      {showAddMetaField && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={5}>
            <TextField
              size="small"
              label="Nombre del campo"
              fullWidth
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              size="small"
              label="Valor"
              fullWidth
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={2}>
            <Box display="flex" alignItems="center" height="100%">
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddMetaField}
                disabled={loading || !newKey.trim() || !newValue.trim()}
                size="small"
              >
                Añadir
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Lista de metadatos */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {metaDataFields.map((field, index) => (
          <Chip
            key={index}
            label={`${field.key}: ${field.value}`}
            onDelete={() => handleRemoveMetaField(index)}
            disabled={loading}
            deleteIcon={<DeleteIcon />}
          />
        ))}
        {metaDataFields.length === 0 && !showAddMetaField && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No hay campos adicionales. Haga clic en "Añadir campo" para agregar información personalizada.
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
          Crear Nueva Columna
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ pt: 1 }}>
          {/* Nombre de la columna */}
          <TextField
            autoFocus
            margin="dense"
            label="Nombre de la columna *"
            type="text"
            fullWidth
            variant="outlined"
            value={columnName}
            onChange={handleNameChange}
            disabled={loading}
            error={!!nameError}
            helperText={nameError}
            sx={{ mb: 2 }}
          />

          {/* Descripción de la columna */}
          <TextField
            margin="dense"
            label="Descripción de la columna"
            type="text"
            fullWidth
            variant="outlined"
            value={columnDescription}
            onChange={handleDescriptionChange}
            disabled={loading}
            multiline
            rows={2}
            placeholder="Añade una descripción para esta columna (opcional)"
            sx={{ mb: 2 }}
          />

          {/* Información de posición automática */}
          <Alert severity="info" sx={{ mb: 2 }}>
            La columna se añadirá en la posición 2, justo después de la columna "Ticket Creado".
          </Alert>

          {/* Sección de metadatos */}
          {renderMetadataSection()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateColumnModal;